import { Helmet } from "react-helmet";
import { useState, useMemo } from "react";
import {
  FaBuilding,
  FaMagnifyingGlass,
  FaUser,
  FaPhone,
  FaPenToSquare,
  FaTrashCan,
  FaFilter,
  FaPlus,
  FaWifi,
  FaWolfPackBattalion, // Icon untuk paket internet
} from "react-icons/fa6";
import { BtnDefault } from "../../../components/buttons/BtnDefault";
import { Header } from "../../../components/Header";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { useHookCustomer } from "../../../hooks/useHookCustomer";

/* ============================================
   DATA DUMMY
   ============================================ */
const dummyCustomers = [
  {
    id: 1,
    company_name: "PT. Sumber Makmur Abadi",
    owner_name: "Budi Santoso",
    phone: "081234567890",
    package: "Internet Dedicated 100 Mbps",
    status: "aktif",
  },
  {
    id: 2,
    company_name: "CV. Karya Sejahtera",
    owner_name: "Siti Aminah",
    phone: "081987654321",
    package: "Home Fiber 50 Mbps",
    status: "aktif",
  },
  {
    id: 3,
    company_name: "Toko Berkah Jaya",
    owner_name: "Ahmad Dahlan",
    phone: "085678901234",
    package: "Business Pro 20 Mbps",
    status: "suspend",
  },
  {
    id: 4,
    company_name: "Warnet Cyber Zone",
    owner_name: "Rizky Ramadhan",
    phone: "08111222333",
    package: "Gaming Bundle 200 Mbps",
    status: "aktif",
  },
  {
    id: 5,
    company_name: "Klinik Sehat Utama",
    owner_name: "Dr. Diana Putri",
    phone: "082233445566",
    package: "Office Basic 10 Mbps",
    status: "nonaktif",
  },
];

/* ============================================
   KOMPONEN: Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 7 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3.5 bg-slate-700/60 rounded-md"
          style={{ width: `${40 + Math.random() * 50}%` }}
        />
      </td>
    ))}
  </tr>
);

/* ============================================
   KOMPONEN: Status Badge
   ============================================ */
const StatusBadge = ({ status }) => {
  const statusConfig = {
    aktif: {
      bg: "bg-emerald-500/12",
      text: "text-emerald-400",
      border: "border-emerald-500/20",
      dot: "bg-emerald-400",
    },
    nonaktif: {
      bg: "bg-slate-500/12",
      text: "text-slate-400",
      border: "border-slate-500/20",
      dot: "bg-slate-400",
    },
    suspend: {
      bg: "bg-orange-500/12",
      text: "text-orange-400",
      border: "border-orange-500/20",
      dot: "bg-orange-400",
    },
  };

  const config = statusConfig[status?.toLowerCase()] || statusConfig.nonaktif;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${config.bg} ${config.text} ${config.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {status || "-"}
    </span>
  );
};

/* ============================================
   KOMPONEN: CustomerPage
   ============================================ */
export const CustomerPage = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [customers, setCustomers] = useState(dummyCustomers);

  /* ---------- Filter & Search (client-side) ---------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPackage, setFilterPackage] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const {
    data,
    meta,
    loading,
    refreshData,
    params,
    setParams,
  } = useHookCustomer();


  /* ---------- Client-side Filter ---------- */
  const filteredData = useMemo(() => {
    return customers.filter((item) => {
      const matchesSearch =
        item?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.phone && item.phone.includes(searchTerm));
      
      const matchesPackage =
        filterPackage === "" || item?.package === filterPackage;
      
      const matchesStatus =
        filterStatus === "" || item?.status === filterStatus;

      return matchesSearch && matchesPackage && matchesStatus;
    });
  }, [customers, searchTerm, filterPackage, filterStatus]);

  /* ---------- Extract unique filters ---------- */
  const packages = useMemo(
    () => [...new Set(customers?.map((i) => i?.package).filter(Boolean))],
    [customers]
  );
  
  const statuses = ["aktif", "nonaktif", "suspend"];

  /* ---------- Actions ---------- */
  const handleDelete = (id) => {
    if (window.confirm("Apakah Anda yakin ingin menghapus data pelanggan ini?")) {
      setCustomers(customers.filter((c) => c.id !== id));
    }
  };

  const handleEdit = (item) => {
    console.log("Edit customer:", item);
    // Logic buka modal edit bisa ditaruh disini
  };

  const handleCreate = () => {
    console.log("Open create modal");
    // Logic buka modal create bisa ditaruh disini
  };

  /* ---------- Column Config ---------- */
  const columns = [
    { key: "no", label: "No", align: "center", width: "60px" },
    { key: "company", label: "Perusahaan", align: "left" },
    { key: "owner", label: "Pemilik", align: "left" },
    { key: "contact", label: "Kontak", align: "left" },
    { key: "email", label: "Email", align: "left" },
    { key: "package", label: "Paket", align: "left" },
    { key: "status", label: "Status", align: "left", width: "130px" },
    { key: "action", label: "Aksi", align: "center", width: "100px" },
  ];

  return (
    <>
      <Helmet>
        <title>Customer Management - DANELA NET</title>
        <meta
          name="description"
          content="Kelola data pelanggan, paket langganan, dan status akun."
        />
      </Helmet>

      <DashboardLayout
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
      >
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <h2 className="text-base md:text-xl font-bold text-gray-100">
              Data Pelanggan
            </h2>
            <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              {filteredData.length} Pelanggan
            </span>
          </div>
          <BtnDefault onClick={handleCreate}>
            <span className="hidden lg:flex items-center gap-2">
              <FaPlus className="text-xs" />
              Tambah Pelanggan
            </span>
            <FaPlus className="lg:hidden" />
          </BtnDefault>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* ===== Search & Filter ===== */}
          <section
            className="rounded-2xl p-4 sm:p-5"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Filter dan pencarian pelanggan"
          >
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              {/* Search */}
              <div className="w-full lg:max-w-xs flex-shrink-0">
                <label
                  htmlFor="search-customer"
                  className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider"
                >
                  Cari Pelanggan
                </label>
                <div className="relative">
                  <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                  <input
                    id="search-customer"
                    type="text"
                    placeholder="Nama perusahaan, pemilik..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <FaFilter className="text-xs text-slate-500" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Filter
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select
                    value={filterPackage}
                    onChange={(e) => setFilterPackage(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-slate-800">
                      Semua Paket
                    </option>
                    {packages.map((pkg) => (
                      <option key={pkg} value={pkg} className="bg-slate-800">
                        {pkg}
                      </option>
                    ))}
                  </select>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-slate-800">
                      Semua Status
                    </option>
                    {statuses.map((s) => (
                      <option key={s} value={s} className="bg-slate-800 capitalize">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Tabel data pelanggan"
          >
            <div className="overflow-x-auto">
              <table className="min-w-max max-w-2xl text-sm">
                {/* Head */}
                <thead>
                  <tr
                    className="border-b"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderColor: "rgba(51, 65, 85, 0.3)",
                    }}
                  >
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${
                          col.align === "center" ? "text-center" : "text-left"
                        }`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody
                  className="divide-y"
                  style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                >
                  {/* Loading */}
                  {loading && (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} cols={columns.length} />
                      ))}
                    </>
                  )}

                  {/* Empty */}
                  {!loading && filteredData?.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{
                              background: "rgba(51, 65, 85, 0.2)",
                              border: "1px solid rgba(51, 65, 85, 0.2)",
                            }}
                          >
                            <FaBuilding className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">
                              Data pelanggan tidak ditemukan
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Coba sesuaikan kata kunci atau filter pencarian.
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Data Rows */}
                  {!loading &&
                    data?.map((item, index) => (
                      <tr
                        key={item.id}
                        className="transition-colors duration-150"
                        style={{ background: "transparent" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "rgba(0, 180, 216, 0.04)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        {/* No */}
                        <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                          {index + 1}
                        </td>

                        {/* Company */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{
                                background:
                                  "linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(8, 145, 178, 0.08))",
                                border: "1px solid rgba(6, 182, 212, 0.15)",
                                color: "#22d3ee",
                              }}
                            >
                              <FaBuilding className="text-sm" />
                            </div>
                            <span className="font-semibold text-gray-100 truncate">
                              {item?.company?.name ?? "-"}
                            </span>
                          </div>
                        </td>

                        {/* Owner */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-slate-300">
                            <FaUser className="text-xs text-slate-500 flex-shrink-0" />
                            <span className="text-sm">{item?.name ?? "-"}</span>
                          </div>
                        </td>

                        {/* Contact */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-slate-400">
                            <FaPhone className="text-xs text-slate-500 flex-shrink-0" />
                            <span className="text-sm font-mono">
                              {item?.phone_number ?? "-"}
                            </span>
                          </div>
                        </td>

                        {/* Email */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-slate-400">
                            <FaPhone className="text-xs text-slate-500 flex-shrink-0" />
                            <span className="text-sm font-mono">
                              {item?.email ?? "-"}
                            </span>
                          </div>
                        </td>

                        {/* Package */}
                        <td className="px-4 py-3">
                           <div className="flex items-center gap-2">
                            <FaWolfPackBattalion className="text-xs text-cyan-500/70" />
                            <span className="text-xs font-medium text-slate-300 capitalize">
                              {item.company?.subscription?.name}
                            </span>
                           </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <StatusBadge status={item.company?.subscription_status} />
                        </td>

                        {/* Action */}
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className="p-2 rounded-lg text-white bg-yellow-500/30 hover:bg-yellow-500/70 transition-all"
                              title="Edit pelanggan"
                            >
                              <FaPenToSquare className="text-sm" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item.id)}
                              className="p-2 rounded-lg text-white bg-red-500/30 hover:bg-red-500/70 transition-all"
                              title="Hapus pelanggan"
                            >
                              <FaTrashCan className="text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== Mobile Card List ===== */}
          <section
            className="lg:hidden space-y-3"
            aria-label="Daftar pelanggan mobile"
          >
            {/* Empty Mobile */}
            {!loading && filteredData?.length === 0 && (
              <div
                className="rounded-2xl p-12 text-center"
                style={{
                  background: "rgba(15, 23, 42, 0.5)",
                  border: "1px solid rgba(51, 65, 85, 0.3)",
                }}
              >
                <p className="text-slate-400 font-medium">Data tidak ditemukan</p>
              </div>
            )}

            {/* Cards */}
            {!loading &&
              filteredData?.map((item, index) => (
                <article
                  key={item.id}
                  className="rounded-2xl overflow-hidden transition-all duration-200"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(0, 180, 216, 0.2)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(51, 65, 85, 0.3)";
                    e.currentTarget.style.background =
                      "rgba(15, 23, 42, 0.5)";
                  }}
                >
                  {/* Header Card */}
                  <div
                    className="px-4 py-3 flex justify-between items-center"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderBottom: "1px solid rgba(51, 65, 85, 0.2)",
                    }}
                  >
                    <div className="flex items-center gap-2 text-slate-500 font-mono text-xs font-bold">
                      #{index + 1}
                    </div>
                    <StatusBadge status={item.status} />
                  </div>

                  {/* Konten Card */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(8, 145, 178, 0.08))",
                            border: "1px solid rgba(6, 182, 212, 0.15)",
                            color: "#22d3ee",
                          }}
                        >
                          <FaBuilding className="text-sm" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-100 leading-tight text-sm truncate">
                            {item.company_name}
                          </h3>
                          <p className="text-xs text-slate-400 truncate mt-0.5">
                            {item.owner_name}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Informasi Detail */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm border-b border-slate-700/30 pb-2">
                        <span className="text-slate-500">Paket</span>
                        <span className="text-cyan-400 font-medium text-right truncate ml-2">
                           {item.package}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <FaPhone className="text-[11px] text-slate-500 flex-shrink-0" />
                        <span className="text-xs font-mono">
                          {item.phone}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div
                      className="flex items-center justify-end pt-3 gap-2"
                      style={{
                        borderTop: "1px solid rgba(51, 65, 85, 0.15)",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => handleEdit(item)}
                        className="p-2.5 rounded-xl text-slate-400 hover:text-yellow-400 transition-all"
                        style={{
                          background: "rgba(234, 179, 8, 0.06)",
                          border: "1px solid rgba(234, 179, 8, 0.1)",
                        }}
                      >
                        <FaPenToSquare className="text-sm" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="p-2.5 rounded-xl text-slate-400 hover:text-red-400 transition-all"
                        style={{
                          background: "rgba(239, 68, 68, 0.06)",
                          border: "1px solid rgba(239, 68, 68, 0.1)",
                        }}
                      >
                        <FaTrashCan className="text-sm" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
          </section>
        </main>
      </DashboardLayout>
    </>
  );
};